import '@cgross/angular-notify';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

//Wrapper around angular-notify: https://github.com/cgross/angular-notify
export class AlertingService {

  defaultDuration = 4000;

  constructor(notify, $translate, $q) {
    this.$translate = $translate;
    this.$q = $q;
    this.notify = notify;
    this.notify.config({ position: 'right', duration: this.defaultDuration });
  }

  _getFinalMessage(message, options) {
    let finalMessage = '';
    if (options && options.noTranslation) {
      finalMessage = message;
    }
    else if (options && options.translationData) {
      finalMessage = this.$translate.instant(message, options.translationData);
    }
    else {
      finalMessage = this.$translate.instant(message);
    }
    return finalMessage;
  }

  /**
    message: string
    options: object: {
        noTranslation: boolean (true for displaying raw message without translating it)
        translationData: object (optional translationData object)
      }
  **/
  info(message, options) {
    let finalMessage = this._getFinalMessage(message, options);
    this.notify({ message: finalMessage, classes: 'alert-alcmeon' });
  }

  success(message, options) {
    let finalMessage = this._getFinalMessage(message, options);
    this.notify({ message: finalMessage, classes: 'alert-alcmeon alert-success' });
  }

  warning(message, options) {
    let finalMessage = this._getFinalMessage(message, options);
    this.notify({ message: finalMessage, classes: 'alert-alcmeon alert-warning' });
  }

  error(message, options) {
    let finalMessage = this._getFinalMessage(message, options);
    const settings = { classes: 'alert-alcmeon alert-danger' };
    if (message === 'bug/unexpected-error') {
      try {
        // Specific code to have date time in case of unexpected error to help the support team.
        settings.messageTemplate = `<div>${finalMessage}</div><div class="details">${new Date().toISOString()}</div>`;
      } catch {
        settings.message = finalMessage;
      }
    } else {
      settings.message = finalMessage;
    }
    // The errors toaster should be closed manually by the user.
    this.notify.config({ duration: 0 });
    this.notify(settings);
    // bad mechanism to reset the duration to the default value. I did not find a better way to do it.
    this.notify.config({ duration: this.defaultDuration });
  }

  errorAndReturnRejectedPromise(message, options) {
    this.error(message, options);
    return this.$q.reject(message);
  }

  alertHTTPError(response) {
    if (response.status !== 401 && response.status !== 403) {
      // 401 and 403 now all redirect to either the login page or the 403 page;
      // let's not show an additional alert in that case
      if (response.data && response.data.error && response.data.error.error_message_translation_key) {
        const options = {
          translationData: response.data.error.error_message_place_holders
        };
        this.error(response.data.error.error_message_translation_key, options);
      } else if (response.data && response.data.error && typeof response.data.error.reason === 'string') {
        this.error(response.data.error.reason);
      } else {
        this.error('common/http_error', {
          translationData: {
            errorCode: response.status
          }
        });
      }
    }
  }

  httpErrorHandler() {
    return (response) => this.alertHTTPError(response);
  }

  /**
  * Convenience method that wraps the error alerting in an rxjs catchError operator which rethrows the error
  */
  catchWebAlertAndThrowHttpError() {
    return catchError((response) => {
      const body = response.error;
      if (body?.error?.error_message_translation_key) {
        this.error(body.error.error_message_translation_key, {
          translationData: body.error.error_message_place_holders,
        });
      } else {
        this.error(response.status === 0 ? 'm/error/unknown-network-error' : 'm/error/unknown-error');
      }
      return EMPTY;
    });
  }


  static AlertingFactory(notify, $translate, $q) {
    return new AlertingService(notify, $translate, $q);
  }

}

AlertingService.AlertingFactory.$inject = ['notify', '$translate', '$q'];
